import React, {
  ReactNode, FC, useState, useEffect, useCallback, useMemo, HTMLAttributes, ReactElement, useRef, useContext,
} from 'react';
import ReactModal, { Styles } from 'react-modal';
import { useDispatch } from 'react-redux';

import getAppContainerId from 'ia-react-core/~/utils/dom/getAppContainerId';
import get from '../../../utils/accessors/get';
import findChildByType from '../../../utils/components/_findChildByType';
import excludeChildrenByType from '../../../utils/components/_excludeChildrenByType';
import getPropByType from '../../../utils/components/_getPropByType';

import closeDrawerById from '../../../actions/drawer-fn/closeDrawerById';
import useScopedSelector from '../../../hooks/useScopedSelector';

import {
  DrawerGlobalStyles, StyledDrawerHeader, StyledTitlesWrapper, StyledTitle, StyledParagraph, StyledCloseButton, StyledDrawerContent, StyledDrawerContentWrapper, StyledFooter,
} from './Drawer.styles';
import { Content, Footer } from './Drawer.compounds';
import RootContext from '../../config/_RootContext';

export interface DrawerProps extends HTMLAttributes<HTMLDivElement> {
  /** @ignore */
  children?: ReactNode;
  /** The name of the drawer. If provided, it will be used as the reducer key. Otherwise the reducer key will be "Drawer{DrawerId}". */
  name?: string;
  /** Position of the drawer. */
  position?: 'left' | 'right';
  /** The title of the drawer. */
  drawerTitle?: ReactNode;
  /** The Subtitle of the drawer. */
  drawerSubTitle?: string;
  /** Removes the padding of the content and footer regions. */
  noPadding?: boolean;
  /** Removes the padding of the header. */
  noHeaderPadding?: boolean;
  /** Removes the padding of the content. */
  noContentPadding?: boolean;
  /** Removes the padding of the footer. */
  noFooterPadding?: boolean;
  /** Do not display an overlay. */
  noOverlay?: boolean;
  /** Defines if the drawer can be closed by either a close button, clicking outside or ESC key. */
  isClosable?: boolean;
  /** Hides the header. */
  noHeader?: boolean;
  /** Sets the initial value of `isOpened`. */
  initialIsOpened?: boolean;
  /** Bottom border of the header. */
  headerBottomBorder?: boolean | string;
  /** Padding of the header. */
  headerPadding?: string;
  /** Padding of the content. */
  contentPadding?: string;
  /** Controls if the Drawer is opened or closed. Marks this drawer as being "controlled". A controlled drawer won't integrate with Redux nor listen to actions. */
  isOpened?: boolean;
  /** Minimum width of the drawer. */
  minWidth?: number;
  /** Show modal when xs. */
  showModalXs?: boolean;
  /** Maximum width of the drawer. */
  maxWidth?: number;
  /** Sets the header to position: sticky. */
  stickyHead?: boolean;
  /** Callback when the drawer opens. */
  'data-analytics-scope'?: string;
  onOpen?: () => void;
  /** Callback when the drawer closes. */
  onClose?: () => void;
}

interface DrawerInternalProps {
  drawerId: number;
}

export interface DrawerComposedProps extends DrawerProps, DrawerInternalProps {}

export type DrawerStatics = {
  Content: typeof Content;
  Footer: typeof Footer;
  appElement?: string | HTMLElement;
  setAppElement: (appElement: string | HTMLElement) => void;
};

export interface DrawerMock {}

const DrawerDefaultProps: Partial<DrawerProps> = {
  position: 'left',
  isClosable: true,
  noHeader: false,
  noPadding: false,
  noHeaderPadding: false,
  noContentPadding: false,
  noFooterPadding: false,
  headerBottomBorder: '',
  minWidth: 320,
  maxWidth: 320,
  stickyHead: false,
  showModalXs: false,
};

/**
 * @since 1.4.0
 */
const Drawer: FC<DrawerComposedProps> & DrawerStatics = Object.assign(
  ({
    drawerId, style, name, position, children, drawerTitle, drawerSubTitle, noHeader, noPadding, noHeaderPadding, noContentPadding, noFooterPadding, isOpened, isClosable, headerBottomBorder, headerPadding, contentPadding, minWidth, showModalXs, maxWidth, noOverlay, stickyHead, onOpen, onClose, ...props
  }: DrawerComposedProps): ReactElement => {
    const { root } = useContext(RootContext);

    // Do not use useEffect for instant execution
    if (!Drawer.appElement) {
      const element: HTMLElement = root.querySelector(`#${getAppContainerId()}`);
      Drawer.setAppElement(element);
    }

    // A quicker state than does not cause updates
    const [isInitialRender, setIsInitialRender] = useState(true);
    const isControlled = useMemo(() => typeof isOpened !== 'undefined', [isOpened]);
    const stateIsOpened: boolean = useScopedSelector(`${name}.isOpened`);
    const resolvedIsOpened = isControlled ? isOpened : stateIsOpened;
    const previousIsOpenedRef = useRef(resolvedIsOpened);
    const dispatch = useDispatch();
    const [actualIsOpened, setActualIsOpened] = useState<boolean>(resolvedIsOpened);

    const content = excludeChildrenByType(children, Footer);
    const contentNoPadding = getPropByType(children, Content, 'noContentPadding', noContentPadding) as boolean;
    const footer = findChildByType(children, Footer);
    const footerNoPadding = get(footer, 'props.noFooterPadding', noFooterPadding);
    const hasFooter = Boolean(footer);

    const scrollRef = useRef(0);
    const scroll = useMemo(() => {
      if (resolvedIsOpened && !actualIsOpened) {
        scrollRef.current = (window.innerWidth - document.body.clientWidth);
      }
      return scrollRef.current;
    }, [resolvedIsOpened, actualIsOpened]);

    const data = useMemo(() => ({ 'analytics-scope': props['data-analytics-scope'] }), [props]);

    const dispatchCloseDrawerById = useCallback(() => dispatch(closeDrawerById(drawerId)), [dispatch, drawerId]);

    const closeDrawer = useCallback(() => {
      if (!isControlled) {
        dispatchCloseDrawerById();
      } else {
        onClose();
      }
    }, [dispatchCloseDrawerById, isControlled, onClose]);

    const finishClosing = useCallback(() => {
      setActualIsOpened(false);
    }, []);

    useEffect(() => {
      if (!isControlled && !resolvedIsOpened && !isInitialRender && previousIsOpenedRef.current !== resolvedIsOpened) {
        onClose?.();
      }

      previousIsOpenedRef.current = resolvedIsOpened;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isControlled, onClose, resolvedIsOpened]);

    useEffect(() => {
      if (resolvedIsOpened) {
        setActualIsOpened(true);
      } else {
        const drawer = root.querySelector(`.iA_Core_Drawer${drawerId}`);
        const overlay = root.querySelector(`.iA_Core_Drawer${drawerId}-Overlay`);

        if (drawer) {
          drawer.addEventListener('transitionend', finishClosing);
          drawer.classList.add('ReactModal__Content--before-close');
          overlay?.classList.add('ReactModal__Overlay--before-close');
        }
      }
    }, [drawerId, finishClosing, resolvedIsOpened, root]);

    useEffect(() => {
      if (!isInitialRender) {
        if (onOpen && actualIsOpened) {
          onOpen();
        }
      } else {
        setIsInitialRender(false);
      }
    }, [isControlled, isInitialRender, actualIsOpened, onClose, onOpen]);

    const isOldIOS = useMemo(() => {
      const iOSVersion = window.navigator.userAgent.match(/iPhone OS ([\d]+)/);
      return (iOSVersion && Number(iOSVersion[1]) <= 14);
    }, []);

    const parentSelector = useCallback(() => (
      (root === document ? root.body : root) as unknown as HTMLElement
    ), [root]);

    return (
      <>
        <DrawerGlobalStyles
          drawerId={drawerId}
          position={position}
          scrollOffset={scroll}
          minWidth={minWidth}
          showModalXs={showModalXs}
          maxWidth={maxWidth}
          isOldIOS={isOldIOS}
          noOverlay={noOverlay}
        />
        <ReactModal
          parentSelector={parentSelector}
          isOpen={actualIsOpened}
          className={`iA_Core_Drawer${drawerId}`}
          overlayClassName={`iA_Core_Drawer${drawerId}-Overlay`}
          onRequestClose={closeDrawer}
          shouldCloseOnOverlayClick={isClosable}
          shouldCloseOnEsc={isClosable}
          data={data}
          style={style as unknown as Styles}
          {...props}
        >
          {!noHeader && (
            <StyledDrawerHeader
              noPadding={noPadding || noHeaderPadding}
              headerBottomBorder={headerBottomBorder}
              headerPadding={headerPadding}
              sticky={stickyHead}
            >
              <StyledTitlesWrapper>
                {drawerTitle && <StyledTitle>{drawerTitle}</StyledTitle>}
                {drawerSubTitle && <StyledParagraph>{drawerSubTitle}</StyledParagraph>}
              </StyledTitlesWrapper>

              {isClosable && (
                <StyledCloseButton
                  data-testid="DrawerClosebutton"
                  onClick={closeDrawer}
                />
              )}
            </StyledDrawerHeader>
          )}
          <StyledDrawerContentWrapper>
            <StyledDrawerContent
              hasFooter={hasFooter}
              noHeader={noHeader}
              noPadding={noPadding || contentNoPadding}
              contentPadding={contentPadding}
            >
              {content.map((child, index) => <React.Fragment key={`${index}`}>{child}</React.Fragment>)}
            </StyledDrawerContent>
            {hasFooter && (
              <StyledFooter noPadding={noPadding || footerNoPadding}>
                {footer}
              </StyledFooter>
            )}
          </StyledDrawerContentWrapper>
        </ReactModal>
      </>
    );
  },
  {
    Content,
    Footer,
    defaultProps: DrawerDefaultProps,
    setAppElement: (appElement: string | HTMLElement) => {
      Drawer.appElement = appElement;
      ReactModal.setAppElement(appElement);
    },
  },
);

export default Drawer;
